// utils/validateInvestment.ts

import { GlobalConfiguration } from '../models/GlobalConfiguration';
import ExtendedLiquid from './ExtendedLiquid';

export interface InvestmentValidationResult {
  validInvestment: number | undefined;
  validationError: string | undefined;
}

/**
 * Validates that a given numeric investment meets the requirements from GlobalConfiguration
 * - is at least the minimum investment amount
 * - is a multiple of the investment increment
 */
export function validateInvestment(
  value?: string | number,
  globalConfig?: GlobalConfiguration | null,
): InvestmentValidationResult {
  if (value == null || value.toString().trim() === '') {
    return {
      validInvestment: undefined,
      validationError: undefined,
    };
  }

  const parsed =
    typeof value === 'number' ? value : parseInt(value.toString().replace(/[^0-9]/g, ''), 10);

  if (isNaN(parsed)) {
    return {
      validInvestment: undefined,
      validationError: undefined,
    };
  }

  // Use GlobalConfiguration values if available, otherwise fall back to defaults
  const minimumInvestment =
    globalConfig?.investorMinimumInvestment ?? globalConfig?.minimumInvestment ?? 100000;
  const investmentIncrement =
    globalConfig?.investorInvestmentIncrement ?? globalConfig?.investmentIncrement ?? 25000;

  if (parsed < minimumInvestment) {
    return {
      validInvestment: undefined,
      validationError: `Minimum investment is ${ExtendedLiquid.currencyMillions(minimumInvestment, false)}`,
    };
  }

  if (parsed % investmentIncrement !== 0) {
    return {
      validInvestment: undefined,
      validationError: `Investment must be in increments of ${ExtendedLiquid.currencyMillions(investmentIncrement, false)}`,
    };
  }

  return {
    validInvestment: parsed,
    validationError: undefined,
  };
}
